import React, { useEffect, useState } from 'react';
import { fi } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import { Helmet } from 'react-helmet';
import { Container, Form } from 'react-bootstrap';
import { SectionHeader, addStatusToast } from '../../common/components';
import { useAppDispatch } from '../../app/hooks';
import { LOCALES } from '../../common/constants';
import {
  GetSalesStatisticsResponse,
  SalesStatisticsFilter,
  getSalesStatisticsAsync,
} from './statisticsSlice';
import {
  getMyAgentNumberAsync,
  GroupsFilter,
  getAllCustomerGroupsAsync,
} from '../Users/identityUsersSlice';
import { useAuthUser } from '../auth/authHooks';
import {
  BookingType,
  bookingTypeTranslations,
} from '../bookings/Constants/BookingTypeEnum';
import { useLocale } from '../../locales/useLocale';
import {
  RegionListItem,
  RegionsFilter,
  getRegionsAsync,
} from '../regions/regionsSlice';
import {
  SkiResortListItem,
  getSkiResortsAsync,
} from '../skiResorts/skiResortsSlice';

const bookingTypeOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 13];
const orderItemTypeOptions = [
  'AccommodationBookingOrderItem',
  'AddOnBookingOrderItem',
  'OfficeFeeBookingOrderItem',
];
const siteItems = [
  { id: '7BF9E2E4-8567-4EF4-84EE-E38B23B72499', name: 'LOMARENGAS' },
  { id: '9826CA69-42DA-4174-B125-E9B29DC327F2', name: 'YLLÄS' },
  { id: '9ECB7F14-13A6-4F29-A3EE-0FCF1977DAEF', name: 'SUOMU' },
];

const sortByName = (a: { name: string }, b: { name: string }) =>
  a.name?.toUpperCase() < b.name?.toUpperCase() ? -1 : 1;

function AgentReport() {
  const dispatch = useAppDispatch();
  const { activeSite } = useAuthUser();
  const { currentLocale } = useLocale();
  const currentLanguage = currentLocale === LOCALES.Fi ? 'fi' : 'en';

  const [officerNumber, setOfficerNumber] = useState<number | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);

  const [regionItems, setRegionItems] = useState<RegionListItem[]>();
  const [skiResortItems, setSkiResortItems] = useState<SkiResortListItem[]>();
  const [customerGroupItems, setCustomerGroupItems] =
    useState<SkiResortListItem[]>();

  const [createdFrom, setCreatedFrom] = useState<Date | null>(null);
  const [createdTo, setCreatedTo] = useState<Date | null>(null);
  const [bookingStartsFrom, setBookingStartsFrom] = useState<Date | null>(null);
  const [bookingStartsTo, setBookingStartsTo] = useState<Date | null>(null);
  const [bookingEndsFrom, setBookingEndsFrom] = useState<Date | null>(null);
  const [bookingEndsTo, setBookingEndsTo] = useState<Date | null>(null);

  const [selectedBookingTypes, setSelectedBookingTypes] = useState<number[]>([
    1, 2, 3, 4, 5, 6, 8, 9, 10,
  ]);
  const [selectedOrderItemTypes, setSelectedOrderItemTypes] =
    useState<string[]>(orderItemTypeOptions);
  const [selectedSites, setSelectedSites] = useState<string[]>(() =>
    activeSite?.id ? [activeSite.id] : [],
  );
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedSkiResorts, setSelectedSkiResorts] = useState<string[]>([]);
  const [selectedCustomerGroups, setSelectedCustomerGroups] = useState<
    string[]
  >([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [settingsLoadFailed, setSettingsLoadFailed] = useState<boolean>(false);
  const [showBookingType, setShowBookingType] = useState(false);
  const [showOrderItemType, setShowOrderItemType] = useState(false);
  const [showSites, setShowSites] = useState(false);
  const [showRegions, setShowRegions] = useState(false);
  const [showSkiResorts, setShowSkiResorts] = useState(false);
  const [showCustomerGroups, setShowCustomerGroups] = useState(false);

  useEffect(() => {
    if (activeSite?.id && selectedSites.length === 0) {
      setSelectedSites([activeSite.id]);
    }
  }, [activeSite?.id, selectedSites.length]);

  useEffect(() => {
    dispatch(getMyAgentNumberAsync()).then(action => {
      if (action.meta.requestStatus !== 'fulfilled') {
        setSettingsLoadFailed(true);
        setSettingsLoaded(true);
        return;
      }
      const agentNumber = action.payload as number | null;
      if (agentNumber != null) {
        setOfficerNumber(agentNumber);
      }
      setSettingsLoaded(true);
    });
  }, [dispatch]);

  useEffect(() => {
    const pageFilter: RegionsFilter = {
      PageNumber: 1,
      PageSize: 1000,
      searchString: '',
    };

    dispatch(getRegionsAsync(pageFilter))
      .then(action => {
        const items = action.payload.data as RegionListItem[];
        setRegionItems(Array.from(items).sort(sortByName));
      })
      .catch(() =>
        dispatch(
          addStatusToast({
            Title: 'Virhe',
            IsFailed: true,
            Message: 'Maakuntien haku ei onnistunut',
          }),
        ),
      );

    dispatch(getSkiResortsAsync(pageFilter))
      .then(action => {
        const items = action.payload.data as SkiResortListItem[];
        setSkiResortItems(Array.from(items).sort(sortByName));
      })
      .catch(() =>
        dispatch(
          addStatusToast({
            Title: 'Virhe',
            IsFailed: true,
            Message: 'Hiihtokeskusten haku ei onnistunut',
          }),
        ),
      );

    const groupsFilter: GroupsFilter = {
      PageNumber: 1,
      PageSize: 1000,
      SearchString: '',
      Culture: 'fi_FI',
    };

    dispatch(getAllCustomerGroupsAsync(groupsFilter))
      .then(action => {
        const items: SkiResortListItem[] = [];
        action.payload.data.forEach(item => {
          const translation = item.translations.find(
            f => f.keyId === 'CustomerGroupName' && f.locale === 'fi_FI',
          );
          items.push({ id: item.id, name: translation?.text });
        });
        setCustomerGroupItems(Array.from(items).sort(sortByName));
      })
      .catch(() =>
        dispatch(
          addStatusToast({
            Title: 'Virhe',
            IsFailed: true,
            Message: 'Asiakasryhmien haku ei onnistunut',
          }),
        ),
      );
  }, [dispatch]);

  const getDateString = (value: Date) => {
    const y = value.getFullYear();
    const m = (value.getMonth() + 1).toString().padStart(2, '0');
    const d = value.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getBookingTypeTranslation = (
    bookingType: BookingType,
    locale: 'en' | 'fi',
  ) => bookingTypeTranslations[locale][bookingType] || 'Tuntematon';

  const getOrderItemTypeName = (value: string) => {
    switch (value) {
      case 'AccommodationBookingOrderItem':
        return 'Majoitus';
      case 'AddOnBookingOrderItem':
        return 'Lisäpalvelut';
      case 'OfficeFeeBookingOrderItem':
        return 'Toimitus';
      default:
        return value;
    }
  };

  const toggle = <T,>(
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    value: T,
  ) =>
    setter(prev =>
      prev.includes(value) ? prev.filter(f => f !== value) : [...prev, value],
    );

  const loadReport = async () => {
    setLoading(true);

    const filter: SalesStatisticsFilter = {
      weekNumbers: false,
      byCountry: false,
      byMonth: false,
      byService: false,
      parseCountryInfo: false,
      byAgent: true,
      byIH: false,
      byIHYllas: false,
      agentNumber: officerNumber,
      bookingType:
        selectedBookingTypes.length > 0 ? selectedBookingTypes : undefined,
      orderItemType:
        selectedOrderItemTypes.length > 0 ? selectedOrderItemTypes : undefined,
      crossSiteIds: selectedSites.length > 0 ? selectedSites : undefined,
      regions: selectedRegions.length > 0 ? selectedRegions : undefined,
      skiResorts:
        selectedSkiResorts.length > 0 ? selectedSkiResorts : undefined,
      customerGroups:
        selectedCustomerGroups.length > 0 ? selectedCustomerGroups : undefined,
    };

    if (createdFrom) filter.created = { from: getDateString(createdFrom) };
    if (createdTo)
      filter.created = { ...filter.created, to: getDateString(createdTo) };
    if (bookingStartsFrom)
      filter.bookingStarts = { from: getDateString(bookingStartsFrom) };
    if (bookingStartsTo)
      filter.bookingStarts = {
        ...filter.bookingStarts,
        to: getDateString(bookingStartsTo),
      };
    if (bookingEndsFrom)
      filter.bookingEnds = { from: getDateString(bookingEndsFrom) };
    if (bookingEndsTo)
      filter.bookingEnds = {
        ...filter.bookingEnds,
        to: getDateString(bookingEndsTo),
      };

    dispatch(getSalesStatisticsAsync(filter))
      .then(action => {
        setLoading(false);
        const response = action.payload as GetSalesStatisticsResponse;
        if (response.status === 200) {
          const link = document.createElement('a');
          link.href = response.blobUrl;
          link.setAttribute('download', response.fileName);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        } else if (response.status === 204) {
          dispatch(
            addStatusToast({
              Title: 'Virhe',
              IsFailed: true,
              Message: 'Hakuehdoilla ei löytynyt varauksia',
            }),
          );
        } else {
          dispatch(
            addStatusToast({
              Title: 'Virhe',
              IsFailed: true,
              Message: 'Edustajaraportin lataus ei onnistunut',
            }),
          );
        }
      })
      .catch(() => {
        setLoading(false);
        dispatch(
          addStatusToast({
            Title: 'Virhe',
            IsFailed: true,
            Message: 'Edustajaraportin lataus ei onnistunut',
          }),
        );
      });
  };

  const renderDropdown = (
    label: string,
    items: { id: string; name: string }[],
    selected: string[],
    showState: boolean,
    setShow: React.Dispatch<React.SetStateAction<boolean>>,
    onToggle: (id: string) => void,
  ) => (
    <div className="position-relative">
      <button
        type="button"
        className="btn btn-outline-secondary w-100 text-start"
        onClick={() => setShow(v => !v)}
      >
        {selected.length > 0 ? (
          <span>{selected.length} valittu</span>
        ) : (
          <span>Kaikki</span>
        )}
      </button>
      {showState && (
        <div
          className="bg-white border rounded mt-1 p-1"
          style={{
            maxHeight: '300px',
            overflow: 'auto',
            zIndex: 1000,
            position: 'absolute',
            width: '100%',
          }}
        >
          {items?.map(opt => (
            <button
              key={opt.id}
              type="button"
              className="dropdown-item text-wrap"
              onClick={() => onToggle(opt.id)}
            >
              <div className="form-check">
                <input
                  type="checkbox"
                  disabled
                  checked={selected.includes(opt.id)}
                  className="form-check-input"
                />{' '}
                {opt.name}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Edustajaraportti</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Edustajaraportti']}
          Title="Edustajaraportti"
        />

        <div className="bg-white p-5 position-relative">
          {!settingsLoaded && (
            <div className="d-flex justify-content-center py-32">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
          {settingsLoaded && settingsLoadFailed && (
            <div className="alert alert-danger" role="alert">
              Virkailijatietojen haku epäonnistui.
            </div>
          )}
          {settingsLoaded && !settingsLoadFailed && !officerNumber && (
            <div className="alert alert-warning" role="alert">
              Virkailijatiedoissasi ei ole edustajanumeroa.
            </div>
          )}
          {settingsLoaded && !settingsLoadFailed && officerNumber && (
            <>
              <div className="row gx-48 gy-24">
                <div className="col-md-4">
                  <Form.Label htmlFor="agentNumber" className="form-label">
                    Edustajanumero
                  </Form.Label>
                  <input
                    type="text"
                    id="agentNumber"
                    className="form-control"
                    value={officerNumber}
                    readOnly
                    disabled
                  />
                </div>
                <div className="w-100 my-n24" />
                <div className="col-6">
                  <Form.Label htmlFor="createdFrom" className="form-label">
                    Varaus tehty
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    <DatePicker
                      locale={fi}
                      dateFormat="dd.MM.yyyy"
                      showYearDropdown
                      id="createdFrom"
                      selected={createdFrom}
                      onChange={setCreatedFrom}
                      className="form-control"
                    />
                    <strong className="px-1">-</strong>
                    <DatePicker
                      locale={fi}
                      dateFormat="dd.MM.yyyy"
                      showYearDropdown
                      id="createdTo"
                      selected={createdTo}
                      onChange={setCreatedTo}
                      className="form-control"
                    />
                  </div>
                </div>
                <div className="col-6">
                  <Form.Label
                    htmlFor="bookingStartsFrom"
                    className="form-label"
                  >
                    Varaus alkaa
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    <DatePicker
                      locale={fi}
                      dateFormat="dd.MM.yyyy"
                      showYearDropdown
                      id="bookingStartsFrom"
                      selected={bookingStartsFrom}
                      onChange={setBookingStartsFrom}
                      className="form-control"
                    />
                    <strong className="px-1">-</strong>
                    <DatePicker
                      locale={fi}
                      dateFormat="dd.MM.yyyy"
                      showYearDropdown
                      id="bookingStartsTo"
                      selected={bookingStartsTo}
                      onChange={setBookingStartsTo}
                      className="form-control"
                    />
                  </div>
                </div>
                <div className="col-6">
                  <Form.Label htmlFor="bookingEndsFrom" className="form-label">
                    Varaus päättyy
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    <DatePicker
                      locale={fi}
                      dateFormat="dd.MM.yyyy"
                      showYearDropdown
                      id="bookingEndsFrom"
                      selected={bookingEndsFrom}
                      onChange={setBookingEndsFrom}
                      className="form-control"
                    />
                    <strong className="px-1">-</strong>
                    <DatePicker
                      locale={fi}
                      dateFormat="dd.MM.yyyy"
                      showYearDropdown
                      id="bookingEndsTo"
                      selected={bookingEndsTo}
                      onChange={setBookingEndsTo}
                      className="form-control"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <Form.Label className="form-label">
                    Varauksen tyyppi
                  </Form.Label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="btn btn-outline-secondary w-100 text-start"
                      onClick={() => setShowBookingType(v => !v)}
                    >
                      <span>{selectedBookingTypes.length} valittu</span>
                    </button>
                    {showBookingType && (
                      <div
                        className="bg-white border rounded mt-1 p-1"
                        style={{
                          maxHeight: '300px',
                          overflow: 'auto',
                          zIndex: 1000,
                          position: 'absolute',
                          width: '100%',
                        }}
                      >
                        {bookingTypeOptions.map(opt => (
                          <button
                            key={`bto-${opt}`}
                            type="button"
                            className="dropdown-item text-wrap"
                            onClick={() => toggle(setSelectedBookingTypes, opt)}
                          >
                            <div className="form-check">
                              <input
                                type="checkbox"
                                disabled
                                checked={selectedBookingTypes.includes(opt)}
                                className="form-check-input"
                              />{' '}
                              {getBookingTypeTranslation(opt, currentLanguage)}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <Form.Label className="form-label">
                    Varausrivin tyyppi
                  </Form.Label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="btn btn-outline-secondary w-100 text-start"
                      onClick={() => setShowOrderItemType(v => !v)}
                    >
                      <span>{selectedOrderItemTypes.length} valittu</span>
                    </button>
                    {showOrderItemType && (
                      <div
                        className="bg-white border rounded mt-1 p-1"
                        style={{
                          maxHeight: '300px',
                          overflow: 'auto',
                          zIndex: 1000,
                          position: 'absolute',
                          width: '100%',
                        }}
                      >
                        {orderItemTypeOptions.map(opt => (
                          <button
                            key={`oit-${opt}`}
                            type="button"
                            className="dropdown-item text-wrap"
                            onClick={() =>
                              toggle(setSelectedOrderItemTypes, opt)
                            }
                          >
                            <div className="form-check">
                              <input
                                type="checkbox"
                                disabled
                                checked={selectedOrderItemTypes.includes(opt)}
                                className="form-check-input"
                              />{' '}
                              {getOrderItemTypeName(opt)}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <Form.Label className="form-label">Asiakasryhmät</Form.Label>
                  {renderDropdown(
                    'Asiakasryhmät',
                    customerGroupItems ?? [],
                    selectedCustomerGroups,
                    showCustomerGroups,
                    setShowCustomerGroups,
                    id => toggle(setSelectedCustomerGroups, id),
                  )}
                </div>
                <div className="col-md-4">
                  <Form.Label className="form-label">Maakunnat</Form.Label>
                  {renderDropdown(
                    'Maakunnat',
                    regionItems ?? [],
                    selectedRegions,
                    showRegions,
                    setShowRegions,
                    id => toggle(setSelectedRegions, id),
                  )}
                </div>
                <div className="col-md-4">
                  <Form.Label className="form-label">
                    Hiihtokeskukset
                  </Form.Label>
                  {renderDropdown(
                    'Hiihtokeskukset',
                    skiResortItems ?? [],
                    selectedSkiResorts,
                    showSkiResorts,
                    setShowSkiResorts,
                    id => toggle(setSelectedSkiResorts, id),
                  )}
                </div>
                <div className="col-md-4">
                  <Form.Label className="form-label">Varaamot</Form.Label>
                  {renderDropdown(
                    'Varaamot',
                    siteItems,
                    selectedSites,
                    showSites,
                    setShowSites,
                    id => toggle(setSelectedSites, id),
                  )}
                </div>
              </div>
              <div className="mt-32 d-flex align-items-center">
                <button
                  type="button"
                  onClick={loadReport}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  <span>Lataa</span>
                </button>
                {loading && (
                  <div
                    className="spinner-border spinner-border-sm ms-3"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Container>
    </>
  );
}

export default AgentReport;
